import { Avatar, Badge, Tooltip } from '@mantine/core';
import { IconMicrophone, IconPhoto } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { MEMORY_KIND_COLOR, memoryKindLabel } from 'src/Components/Memories/memoryKinds';
import { MemoryKind, MemoryTimelineItemDto } from 'src/Services/API/Memories.api';
import { formatMemoryDate } from 'src/Utils/memoryDate';

import styles from './MemoryCard.module.scss';

// Who was there: a few first names, the rest as « +3 », so the card stays readable.
const PEOPLE_SHOWN = 4;

interface Props {
  memory: MemoryTimelineItemDto;
  onClick: () => void;
}

const MemoryCard = ({ memory, onClick }: Props) => {
  const { t } = useTranslation();
  const isInMemory = memory.memoryKind === MemoryKind.InMemory;
  const dateLabel = formatMemoryDate(
    t,
    memory.takenAt,
    memory.takenAtPrecision,
    memory.takenAtApproximate
  );
  const context = [
    memory.rooms.map(room => room.name).join(', '),
    memory.photoCount > 0
      ? t('memories.card.photos', { count: memory.photoCount, defaultValue: '{{count}} photos' })
      : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const coverUrl = memory.cover?.previewMediumUrl ?? memory.cover?.previewThumbnailUrl;

  return (
    <button
      type="button"
      className={`${styles.card} ${isInMemory ? styles.inMemory : ''}`}
      onClick={onClick}
    >
      <div className={styles.cover}>
        {coverUrl ? (
          <img src={coverUrl} alt="" loading="lazy" />
        ) : (
          <div className={styles.coverPlaceholder}>
            <IconPhoto size={28} />
          </div>
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.topRow}>
          {dateLabel && <span className={styles.date}>{dateLabel}</span>}
          {memory.memoryKind && !isInMemory && (
            <Badge size="sm" variant="light" color={MEMORY_KIND_COLOR[memory.memoryKind]}>
              {memoryKindLabel(t, memory.memoryKind)}
            </Badge>
          )}
          {isInMemory && (
            <span className={styles.kindPlain}>{memoryKindLabel(t, memory.memoryKind)}</span>
          )}
        </div>
        <span className={styles.title}>{memory.title}</span>
        {context && <span className={styles.context}>{context}</span>}
        {memory.people.length > 0 && (
          <div className={styles.peopleRow}>
            <Avatar.Group spacing="xs">
              {memory.people.slice(0, PEOPLE_SHOWN).map(person => (
                <Avatar key={person.id} size="sm" color="initials" name={person.displayName} />
              ))}
            </Avatar.Group>
            <span className={styles.context}>
              {memory.people
                .slice(0, PEOPLE_SHOWN)
                .map(person => person.displayName)
                .join(', ')}
              {memory.people.length > PEOPLE_SHOWN
                ? ` ${t('memories.card.morePeople', '+{{count}}', {
                    count: memory.people.length - PEOPLE_SHOWN,
                  })}`
                : ''}
            </span>
          </div>
        )}
        {memory.recordingCount > 0 && (
          <div className={styles.voiceRow}>
            <span className={styles.voicePill}>
              <IconMicrophone size={16} />
              {t('memories.card.recordings', {
                count: memory.recordingCount,
                defaultValue: '{{count}} récits à écouter',
              })}
            </span>
            <Avatar.Group spacing="xs">
              {memory.storytellers.map(storyteller => (
                <Tooltip key={storyteller.displayName} label={storyteller.displayName} withArrow>
                  <Avatar size="sm" color="initials" name={storyteller.displayName}>
                    {storyteller.initials}
                  </Avatar>
                </Tooltip>
              ))}
            </Avatar.Group>
          </div>
        )}
      </div>
    </button>
  );
};

export default MemoryCard;
